<script setup>
import { computed } from 'vue'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import { useI18n } from '@/i18n'

const props = defineProps({
  workflow: { type: Object, required: true },
  canEdit:  { type: Boolean, default: false },
})

const emit = defineEmits(['edit'])

const { t } = useI18n()

const severityTag = (s) => {
  switch (s) {
    case 'INFO':     return 'secondary'
    case 'LOW':      return 'info'
    case 'MEDIUM':   return 'warn'
    case 'HIGH':     return 'danger'
    case 'CRITICAL': return 'contrast'
    default:         return null
  }
}

const visibilityTag = computed(() => {
  switch (props.workflow.visibility) {
    case 'company':   return { label: t('siemWorkflows.visCompany'),   severity: 'secondary', hint: t('siemWorkflows.visCompanyHint')   }
    case 'public':    return { label: t('siemWorkflows.visPublic'),     severity: 'success',   hint: t('siemWorkflows.visPublicHint')    }
    default:          return { label: t('siemWorkflows.visCommunity'),  severity: 'info',      hint: t('siemWorkflows.visCommunityHint') }
  }
})
</script>

<template>
  <div class="wf-header">
    <div class="wf-header-main">
      <h2 class="wf-title">{{ workflow.name }}</h2>
      <p v-if="workflow.category_name || workflow.category_id" class="wf-subtitle">
        {{ workflow.category_name || `#${workflow.category_id}` }}
      </p>
    </div>

    <div class="wf-header-meta">
      <!-- Severity -->
      <Tag :value="workflow.severity" :severity="severityTag(workflow.severity)" />

      <!-- Visibility -->
      <Tag
        :value="visibilityTag.label"
        :severity="visibilityTag.severity"
        v-tooltip.bottom="visibilityTag.hint"
      />

      <!-- Company attribution -->
      <span v-if="workflow.company_name" class="wf-company">
        <i class="pi pi-building" />
        {{ workflow.company_name }}
      </span>

      <!-- Featured star -->
      <span v-if="workflow.featured" class="wf-featured" v-tooltip.bottom="t('siemWorkflows.formFeatured')">
        <i class="pi pi-star-fill" />
      </span>

      <!-- Edit button -->
      <Button
        v-if="canEdit"
        :label="t('siemWorkflowDetails.editWorkflow')"
        icon="pi pi-pencil"
        size="small"
        severity="secondary"
        outlined
        @click="emit('edit')"
      />
    </div>
  </div>
</template>

<style scoped>
.wf-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
}

.wf-header-main {
  flex: 1;
  min-width: 0;
}

.wf-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--p-text-color);
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wf-subtitle {
  margin: 3px 0 0;
  font-size: .82rem;
  color: var(--p-text-color-secondary);
}

.wf-header-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.wf-company {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: .78rem;
  font-weight: 600;
  color: var(--p-text-color-secondary);
}

.wf-company .pi {
  font-size: .7rem;
  opacity: .6;
}

.wf-featured {
  display: inline-flex;
  align-items: center;
  color: var(--p-warn-500, #f59e0b);
  font-size: .9rem;
}
</style>
